import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProjectStatusConstants } from "../../../../../constants";
import { CursorChangeProps, ResultType } from "../../../../../models";
import { searchProjects, SearchProjectsResponse } from "../../../../../service/query";
import { ResultData } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { flattenObject } from "../../../../../utils";
import { getProjectDetailsByUuid, getProjectsRoute } from "../../../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../../widget";

interface UseDashboardProjectsTabReturnData {
  columns: DataGridColumnDefinition[];
  isLoading: boolean;
  onChange: DataGridCursorDataLoadEventHandler;
  onViewMore: () => void;
}

export const useDashboardProjectsTab = (): UseDashboardProjectsTabReturnData => {
  const { currentUserType } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const onViewMore = (): void => navigate(getProjectsRoute(currentUserType));

  const columns: DataGridColumnDefinition[] = [
    {
      key: "link",
      name: "Project",
      dataType: "string",
      formatter: "link",
      sortable: false,
    },
    {
      key: "standard.displayName",
      name: "Code",
      dataType: "string",
      sortable: false,
    },
    {
      key: "developer.displayName",
      name: "Developer",
      dataType: "string",
      sortable: false,
    },
    {
      name: ProjectStatusConstants.REGISTRY_STATUS_LABEL,
      key: "status",
      dataType: "string",
      formatter: "projectStatusPill",
      minWidth: 200,
      sortable: false,
    },
    {
      name: "Total units",
      key: "cachedUnitTotal",
      dataType: "string",
      filterable: false,
      minWidth: 112,
      sortable: false,
    },
    {
      name: "Available",
      key: "cachedUnitAvailableForSale",
      dataType: "string",
      filterable: false,
      minWidth: 112,
      sortable: false,
    },
    {
      name: "Reserved",
      key: "cachedUnitReserved",
      dataType: "string",
      filterable: false,
      minWidth: 112,
      sortable: false,
    },
    {
      name: "Sold",
      key: "cachedUnitSold",
      dataType: "string",
      filterable: false,
      minWidth: 112,
      sortable: false,
    },
  ];

  const formatData = useCallback(
    (responseData: SearchProjectsResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.link = {
          text: el.displayName,
          to: getProjectDetailsByUuid(el.uuid, currentUserType),
        } as DataGridLinkCellFormatterData;

        return result;
      }) || [],
    []
  );

  const onChange: DataGridCursorDataLoadEventHandler = async ({ filtering, paging }: CursorChangeProps) => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.link) {
      filterCriteria.displayName = filterCriteria.link;
      filterCriteria.link = undefined;
    }

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 10,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    setIsLoading(true);

    await searchProjects({
      paging: {
        limit: 10,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [
        {
          key: "cachedUnitTotal" as "results.cachedUnitTotal",
          direction: "desc",
        },
      ],
      filter: { results: filterCriteria },
    })
      .then((response) => {
        const resultData = formatData(response.data);
        data = {
          resultData,
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 10,
            // Dashboard preview: report the displayed row count, not the full total.
            totalCount: resultData.length,
          },
        };
      })
      .finally(() => {
        setIsLoading(false);
      });
    return data;
  };

  return {
    columns,
    isLoading,
    onChange,
    onViewMore,
  };
};
